// The local half of company pages.
//
// A page is cached so it opens without a connection — reading a catalogue on a train is
// the ordinary case, not an edge one. What the cache must never do is let someone ACT on
// a stale page: the seller is the only source of truth about price, and a cached card can
// show one that has moved. That rule is enforced where the page is drawn
// (`ViewScreen.svelte` passes `disabled` into the element context), not here; this module
// only records how fresh each body is so the screen can decide.

import { db, type ViewRow } from './db';

import { getView, listViews } from '$lib/protocol/distributor-api';
import { ClientError } from '$lib/protocol/errors';
import { sessionToken } from '$lib/protocol/session';
import type { CompanyView, MessageElement, ViewDescriptor } from '$lib/protocol/types';
import { parseElements } from '$lib/protocol/types';

const rowId = (accountId: string, viewId: string): string => `${accountId}/${viewId}`;

export async function cachedViews(accountId: string): Promise<ViewRow[]> {
  const rows = await db.views.where('accountId').equals(accountId).toArray();
  return rows.sort((a, b) => a.position - b.position);
}

export async function cachedView(
  accountId: string,
  viewId: string,
): Promise<ViewRow | undefined> {
  return await db.views.get(rowId(accountId, viewId));
}

export function elementsOf(row: ViewRow | undefined): MessageElement[] {
  if (row?.elementsJson === null || row?.elementsJson === undefined) {
    return [];
  }
  try {
    return parseElements(JSON.parse(row.elementsJson));
  } catch {
    // A body we cannot parse is a body we do not have. The screen then shows the
    // empty state rather than half a page.
    return [];
  }
}

/** Reconciles the cached menu with what the server just listed. Pages absent from the
 *  list are deleted outright: a page that was withdrawn is not news and must not linger
 *  in a menu. A descriptor whose version matches keeps its cached body. */
export async function replaceViewList(
  accountId: string,
  descriptors: ViewDescriptor[],
): Promise<void> {
  await db.transaction('rw', db.views, async () => {
    const existing = await db.views.where('accountId').equals(accountId).toArray();
    const kept = new Set(descriptors.map((d) => d.viewId));
    for (const row of existing) {
      if (!kept.has(row.viewId)) {
        await db.views.delete(row.id);
      }
    }
    for (const [i, descriptor] of descriptors.entries()) {
      const previous = existing.find((r) => r.viewId === descriptor.viewId);
      const stale = previous === undefined || previous.version !== descriptor.version;
      await db.views.put({
        id: rowId(accountId, descriptor.viewId),
        accountId,
        viewId: descriptor.viewId,
        title: descriptor.title,
        version: descriptor.version,
        position: i,
        // A body from an older version is not shown as if it were the new one.
        elementsJson: stale ? null : (previous?.elementsJson ?? null),
        fetchedAtMs: stale ? null : (previous?.fetchedAtMs ?? null),
      });
    }
  });
}

export async function storeViewBody(
  accountId: string,
  view: CompanyView,
  rawElements: unknown,
  nowMs: number,
): Promise<void> {
  const id = rowId(accountId, view.viewId);
  const previous = await db.views.get(id);
  await db.views.put({
    id,
    accountId,
    viewId: view.viewId,
    title: view.title,
    version: view.version,
    position: previous?.position ?? 0,
    elementsJson: JSON.stringify(rawElements),
    fetchedAtMs: nowMs,
  });
}

export async function forgetView(accountId: string, viewId: string): Promise<void> {
  await db.views.delete(rowId(accountId, viewId));
}

export async function forgetViewsFor(accountId: string): Promise<void> {
  await db.views.where('accountId').equals(accountId).delete();
}

// ——————————————————————————————————————————————————————————————
// Fetching
// ——————————————————————————————————————————————————————————————

/** Refreshes the menu for one contact. Called when `views_version` moves and when a
 *  contact's conversation opens — never on a timer, because a page nobody is looking at
 *  is not worth a request. */
export async function refreshViewList(accountId: string): Promise<void> {
  const contact = await db.contacts.get(accountId);
  if (contact === undefined || contact.needsReverify) {
    return;
  }
  const token = await sessionToken(contact);
  const descriptors = await listViews({
    serverUrl: contact.serverUrl,
    accountId,
    token,
  });
  await replaceViewList(accountId, descriptors);
}

export interface LoadedView {
  row: ViewRow | undefined;
  /** False when this body came from the cache without a successful fetch. The screen
   *  turns every control off in that case — reading a stale catalogue is harmless,
   *  ordering against a stale price is not. */
  fresh: boolean;
}

/** Opening a page always attempts a fetch. On failure the cached body is still returned,
 *  marked stale, and the screen renders it read-only rather than showing nothing. */
export async function loadView(accountId: string, viewId: string): Promise<LoadedView> {
  const contact = await db.contacts.get(accountId);
  if (contact === undefined) {
    return { row: undefined, fresh: false };
  }
  try {
    const token = await sessionToken(contact);
    const view = await getView({ serverUrl: contact.serverUrl, accountId, token, viewId });
    await storeViewBody(accountId, view, view.rawElements, Date.now());
    return { row: await cachedView(accountId, viewId), fresh: true };
  } catch (error) {
    // A page the server no longer serves is dropped without a word: a page that was
    // withdrawn is not news, and telling the buyer would make it one.
    if (error instanceof ClientError && error.code === 'not_found') {
      await forgetView(accountId, viewId);
      return { row: undefined, fresh: true };
    }
    return { row: await cachedView(accountId, viewId), fresh: false };
  }
}
